import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { BarChart3, DollarSign, Inbox, Search, SlidersHorizontal, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import { useFundingRoundsPagination } from '@/hooks/useFundingRoundsPagination'
import { useProjectsStats } from '@/hooks/useOTC'
import { formatCompactNumber, formatCurrency } from '@/utils'
import type { Project } from '@/types/project'

type MetricCardProps = {
  icon: ReactNode
  label: string
  value: ReactNode
  change?: {
    value: string
    direction: 'up' | 'down'
    color: 'green' | 'red'
  }
}

const ChangeBadge = ({ change }: { change?: MetricCardProps['change'] }) => {
  if (!change) return null

  const isUp = change.direction === 'up'

  return (
    <div className="flex items-center gap-[4px] rounded-[16px] bg-[rgba(242,241,246,0.64)] pb-[4px] pl-[6px] pr-[8px] pt-[3px] shadow-[inset_1px_1px_4px_0px_rgba(217,214,227,0.4)] dark:bg-[rgba(45,41,35,0.24)]">
      <span className={`text-[12px] leading-[16px] ${change.color === 'green' ? 'text-[#2ea66b]' : 'text-[#ff540c]'}`}>
        {isUp ? '↗' : '↘'}
      </span>
      <span className={`font-plus-jakarta text-[12px] font-semibold leading-[16px] ${change.color === 'green' ? 'text-[#2ea66b]' : 'text-[#ff540c]'}`}>
        {change.value}
      </span>
    </div>
  )
}

const MobileMetricCard = ({ icon, label, value, change }: MetricCardProps) => {
  return (
    <div className="h-[110px] bg-white p-4 dark:bg-[#1c1c1c]">
      <div className="flex h-full flex-col justify-between">
        <div className="space-y-[6px]">
          <div className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">{icon}</div>
          <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {label}
          </p>
        </div>

        <div className="flex items-center gap-[6px]">
          <div className="font-plus-jakarta text-[20px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            {value}
          </div>
          <ChangeBadge change={change} />
        </div>
      </div>
    </div>
  )
}

const formatTableDate = (date: string | null | undefined) => {
  if (!date) return '－'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return '－'

  return parsed.toLocaleDateString('en-GB')
}

const formatMoneyCell = (value: number | null | undefined) => {
  const formatted = formatCurrency(value)
  return formatted === '—' ? '－' : formatted
}

const FundingRoundsMobileRowCard = ({ item, onClick }: { item: Project; onClick: (item: Project) => void }) => {
  const latestRound = useMemo(
    () => (item.rounds || []).slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0],
    [item.rounds],
  )

  const totalRaised = item.stats?.total_funding_raised ?? (item.rounds || []).reduce((sum, round) => sum + (round.amount_raised || 0), 0)
  const latestRaised = item.stats?.last_round_raised_amount ?? latestRound?.amount_raised
  const valuation = item.stats?.most_recent_valuation ?? latestRound?.fully_diluted_valuation

  return (
    <button type="button" onClick={() => onClick(item)} className="w-full min-h-[160px] bg-white p-3 text-left dark:bg-[#1c1c1c]">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="size-[36px] shrink-0 overflow-hidden rounded-full bg-[var(--base-muted,#f5f5f5)] dark:bg-[#2a2a2a]">
            {item.logo ? (
              <img
                src={item.logo}
                alt={item.project_name}
                className="h-full w-full object-cover"
                onError={(event) => {
                  const target = event.target as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
            ) : (
              <div className="h-full w-full bg-gray-200 dark:bg-gray-700" />
            )}
          </div>

          <p className="truncate font-plus-jakarta text-[16px] font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
            {item.project_name}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Total Raised
            </p>
            <p className="font-plus-jakarta text-[13px] font-normal leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatMoneyCell(totalRaised)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Latest Round
            </p>
            <p className="font-plus-jakarta text-[13px] font-normal leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatMoneyCell(latestRaised)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Valuation
            </p>
            <p className="font-plus-jakarta text-[13px] font-normal leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatMoneyCell(valuation)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Data
            </p>
            <p className="font-plus-jakarta text-[13px] font-normal leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatTableDate(latestRound?.date)}
            </p>
          </div>
        </div>
      </div>
    </button>
  )
}

const FundingRoundsScreenMobile = () => {
  const navigate = useNavigate()
  const { data: stats } = useProjectsStats()
  const { projects, loading, search, setSearch } = useFundingRoundsPagination()
  const [localSearch, setLocalSearch] = useState(search || '')

  useEffect(() => {
    setLocalSearch(search || '')
  }, [search])

  const totalProjectsChange = useMemo(() => {
    if (stats?.total_projects_added_30d_pct_change == null) return undefined

    const isUp = (stats.total_projects_added_30d ?? 0) >= 0
    return {
      value: `${Math.abs(stats.total_projects_added_30d_pct_change).toFixed(1)}%`,
      direction: isUp ? 'up' : 'down',
      color: isUp ? 'green' : 'red',
    } as const
  }, [stats?.total_projects_added_30d, stats?.total_projects_added_30d_pct_change])

  const roundsChange = useMemo(() => {
    if (stats?.total_funding_rounds_added_30d_pct_change == null) return undefined

    const isUp = (stats.total_funding_rounds_added_30d ?? 0) >= 0
    return {
      value: `${Math.abs(stats.total_funding_rounds_added_30d_pct_change).toFixed(0)}%`,
      direction: isUp ? 'up' : 'down',
      color: isUp ? 'green' : 'red',
    } as const
  }, [stats?.total_funding_rounds_added_30d, stats?.total_funding_rounds_added_30d_pct_change])

  const investorsChange = useMemo(() => {
    if (stats?.total_investors_added_30d == null || stats.total_investors_added_30d_pct_change == null) return undefined

    const isUp = stats.total_investors_added_30d >= 0
    return {
      value: `${Math.abs(stats.total_investors_added_30d)} (${Math.abs(stats.total_investors_added_30d_pct_change).toFixed(1)}%)`,
      direction: isUp ? 'up' : 'down',
      color: isUp ? 'green' : 'red',
    } as const
  }, [stats?.total_investors_added_30d, stats?.total_investors_added_30d_pct_change])

  const avgFundingRoundSize = stats?.average_funding_round_size ?? null
  const avgFundingRoundLabel = avgFundingRoundSize == null
    ? '—'
    : (
      <>
        <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>{' '}
        {formatCompactNumber(avgFundingRoundSize)}
      </>
    )

  const handleSearchChange = (value: string) => {
    setLocalSearch(value)
    setSearch(value)
  }

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        <div className="flex flex-col items-start">
          <h1 className="font-plus-jakarta text-[23px] font-semibold leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            Funding Rounds
          </h1>
          <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            Access institutional-grade project funding data
          </p>
        </div>

        <div className="grid grid-cols-2 gap-[6px]">
          <MobileMetricCard
            icon={<Inbox size={16} />}
            label="Total Projects"
            value={formatCompactNumber(stats?.total_projects)}
            change={totalProjectsChange}
          />
          <MobileMetricCard
            icon={<BarChart3 size={16} />}
            label="Total Funding Rounds"
            value={formatCompactNumber(stats?.total_funding_rounds)}
            change={roundsChange}
          />
          <MobileMetricCard icon={<DollarSign size={16} />} label="Avg. Funding Round Size" value={avgFundingRoundLabel} />
          <MobileMetricCard
            icon={<Users size={16} />}
            label="Total Investors"
            value={formatCompactNumber(stats?.total_investors)}
            change={investorsChange}
          />
        </div>

        <div className="space-y-[6px]">
          <h3 className="font-plus-jakarta text-[20px] font-bold leading-[24px] tracking-[-0.2px] text-[#27292d] dark:text-[#fdfdfd]">
            Funding Rounds
          </h3>

          <div className="border border-white bg-[rgba(255,255,255,0.32)] p-2 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.32)]">
            <div className="flex h-[54px] items-center gap-2">
              <div className="flex h-full flex-1 items-center gap-2 bg-[rgba(255,255,255,0.44)] px-3 py-[18px] dark:bg-[rgba(28,28,28,0.44)]">
                <Search size={16} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />
                <input
                  type="text"
                  value={localSearch}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  placeholder="Search by project name"
                  className="w-full bg-transparent font-plus-jakarta text-[13px] font-normal leading-[18px] text-[rgba(45,41,35,0.48)] outline-none placeholder:text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] dark:placeholder:text-[rgba(253,253,253,0.48)]"
                />
              </div>

              <button
                type="button"
                className="flex size-[54px] items-center justify-center rounded-full border border-[rgba(45,41,35,0.08)] bg-[rgba(255,255,255,0.62)] dark:border-[rgba(253,253,253,0.16)] dark:bg-[rgba(253,253,253,0.12)]"
                aria-label="Open filters"
              >
                <SlidersHorizontal size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
              </button>
            </div>

            <div className="mt-3 space-y-2">
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={`mobile-funding-skeleton-${index}`} className="h-[160px] w-full animate-pulse bg-white/80 dark:bg-[#1c1c1c]" />
                ))
              ) : projects.length === 0 ? (
                <div className="bg-white p-6 text-center font-plus-jakarta text-[14px] leading-[20px] text-[rgba(45,41,35,0.72)] dark:bg-[#1c1c1c] dark:text-[rgba(253,253,253,0.72)]">
                  No funding rounds found
                </div>
              ) : (
                projects.map((item) => (
                  <FundingRoundsMobileRowCard
                    key={item.project_id}
                    item={item}
                    onClick={(project) => navigate(`/data-rooms/${project.project_id}`)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </PageTemplate>
  )
}

export default FundingRoundsScreenMobile
